import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Typography from 'LuxKit/Typography/Typography'
import type { LoyaltyLandingPageBenefitCopy } from 'loyalty/pages/LoyaltyLandingPageData'
import LoyaltyLandingPageLargeTile from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageLargeTile'
import LoyaltyLandingPageSmallTile from 'loyalty/pages/loyaltyLandingPageSharedSections/LoyaltyLandingPageSmallTile'
import { EmptyArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Section = styled(Pane)`
  padding: ${rem(24)} 0;

  ${mediaQueryUp.tablet} {
    padding: ${rem(48)} 0;
  }
`

const SmallTilesGrid = styled.div`
  display: grid;
  gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  benefitCopy?: LoyaltyLandingPageBenefitCopy
  onCtaClick?: () => void
  ctaText?: string
}

function TierUpgradeCampaignBenefitsShowcase(props: Props) {
  const intl = useIntl()
  const { benefitCopy, onCtaClick, ctaText } = props

  const largeTiles = useMemo(() => {
    if (!benefitCopy) return EmptyArray
    return [
      benefitCopy.hotel_upgrade,
      benefitCopy.airport_transfer,
      benefitCopy.lounge_pass,
    ].filter(tile => tile?.enabled)
  }, [benefitCopy])

  const smallTiles = useMemo(() => {
    if (!benefitCopy) return EmptyArray
    return [
      benefitCopy.phone_support,
      benefitCopy.travelshoot_credit,
      benefitCopy.tour_exp_credit,
    ].filter(tile => tile?.enabled)
  }, [benefitCopy])

  const sectionHeading = intl.formatMessage({
    id: '6sf-0hamGkMXLxy_p_tH',
    defaultMessage: 'Where loyalty meets luxury',
    description: 'Heading for the benefits showcase section on tier upgrade campaign page',
  })
  const sectionDescription = intl.formatMessage({
    id: 'HSyNu4grN83g5VfBN1Gd',
    defaultMessage: 'Every booking brings you closer to unlocking VIP benefits, adding more luxury to every escape.',
    description: 'Description for the benefits showcase section on tier upgrade campaign page',
  })
  if (largeTiles.length === 0 && smallTiles.length === 0) return null

  return <Section background="normal-background">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={24}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading2" align="center">{sectionHeading}</Heading>
          <BodyText variant="large" align="center">{sectionDescription}</BodyText>
        </VerticalSpacer>
        <VerticalSpacer gap={24}>
          {largeTiles.map((tile, index) => <LoyaltyLandingPageLargeTile
            key={tile.benefitCode}
            tile={tile}
            reverse={index % 2 === 0}
            background="light-background"
          />)}
          {smallTiles.length > 0 && <SmallTilesGrid>
            {smallTiles.map(tile => <LoyaltyLandingPageSmallTile
              key={tile.benefitCode}
              tile={tile}
            />)}
          </SmallTilesGrid>}
        </VerticalSpacer>
        {onCtaClick && <Typography align="center">
          <TextButton kind="primary" size="large" onClick={onCtaClick}>
            {ctaText}
          </TextButton>
        </Typography>}
      </VerticalSpacer>
    </LayoutContainer>
  </Section>
}

export default TierUpgradeCampaignBenefitsShowcase
